import { Injectable, inject, signal, computed, Signal } from '@angular/core';
import { forkJoin, Observable, firstValueFrom } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import {
  MasterDataRepositoryService,
  GenericCode,
  PlaceOfService,
  MasterPermission,
  InsertableValue,
  InsertableValueEntity,
  InsertableValuesStore,
  StateStore, StateStorePromises
} from '@xmedical/core';
import { CacheService } from '../services/cache.service';
import { CoreEventsService } from '../services/core-events.service';

/**
 * MasterDataStore
 *
 * Manages global master/reference data that is loaded once per session.
 * Uses Angular signals for reactive state management.
 *
 * Data managed:
 * - Patient genders (Mr, Mrs, Ms, Dr, etc)
 * - Patient titles
 * - Patient races
 * - Patient languages
 * - Places of service
 * - Master permissions (all available system permissions)
 *
 * All data is cached in localStorage with no expiration.
 * Each store registers its own boot steps for initialization tracking.
 */
@Injectable({
  providedIn: 'root'
})
export class MasterDataStore implements StateStore, StateStorePromises {
  private masterDataRepo = inject(MasterDataRepositoryService);
  private cache = inject(CacheService);
  private events = inject(CoreEventsService);
  private insertableValuesStore = inject(InsertableValuesStore);

  // State signals
  readonly patientGenders = signal<GenericCode[]>([]);
  readonly patientTitles = signal<GenericCode[]>([]);
  readonly patientRaces = signal<GenericCode[]>([]);
  readonly patientLanguages = signal<GenericCode[]>([]);
  readonly placesOfService = signal<PlaceOfService[]>([]);
  readonly masterPermissions = signal<MasterPermission[]>([]);
  readonly loaded = signal<boolean>(false);
  readonly loading = signal<boolean>(false);

  // Promise for tracking in-flight load operation
  loadingPromise: Promise<void> | null = null;

  // Computed signals
  readonly isReady = computed(() => this.loaded());

  /**
   * Initialize all master data
   * Loads all master data in parallel and caches results
   *
   * @returns Observable that completes when all data is loaded
   */
  initialize(): Observable<void> {
    this.loading.set(true);

    // Load all data in parallel
    return forkJoin({
      genders: this.loadPatientGenders(),
      titles: this.loadPatientTitles(),
      races: this.loadPatientRaces(),
      languages: this.loadPatientLanguages(),
      places: this.loadPlacesOfService(),
      permissions: this.loadMasterPermissions()
    }).pipe(
      tap(() => {
        this.loaded.set(true);
        this.loading.set(false);
      }),
      map(() => void 0)
    );
  }

  /**
   * Ensure master data is loaded - on-demand loading for page refresh scenarios.
   * - If already loaded, returns immediately
   * - If loading in progress, waits for completion
   * - If not loaded, triggers fetch and waits
   *
   * @returns Promise that resolves when master data is available
   */
  async ensureLoaded(): Promise<void> {
    if (this.loaded()) return;

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // Not loaded and not loading - trigger initialization
    this.loadingPromise = firstValueFrom(this.initialize()).finally(() => {
      this.loadingPromise = null;
    });

    return this.loadingPromise;
  }

  /**
   * Load patient genders
   * Uses cache-first strategy with no TTL
   */
  private loadPatientGenders(): Observable<GenericCode[]> {
    return this.cache.cacheFirst(
      'patient-genders',
      () => this.masterDataRepo.genders(),
      {
        onCacheHit: (genders) => {
          this.patientGenders.set(genders);

        },
        onFetchComplete: (genders) => {
          this.patientGenders.set(genders);

        }
      }
    );
  }

  /**
   * Load patient titles
   * Uses cache-first strategy with no TTL
   */
  private loadPatientTitles(): Observable<GenericCode[]> {
    return this.cache.cacheFirst(
      'patient-titles',
      () => this.masterDataRepo.titles(),
      {
        onCacheHit: (titles) => {
          this.patientTitles.set(titles);

        },
        onFetchComplete: (titles) => {
          this.patientTitles.set(titles);

        }
      }
    );
  }

  /**
   * Load patient races
   * Uses cache-first strategy with no TTL
   */
  private loadPatientRaces(): Observable<GenericCode[]> {
    return this.cache.cacheFirst(
      'patient-races',
      () => this.masterDataRepo.races(),
      {
        onCacheHit: (races) => {
          this.patientRaces.set(races);

        },
        onFetchComplete: (races) => {
          this.patientRaces.set(races);

        }
      }
    );
  }

  /**
   * Load patient languages
   * Uses cache-first strategy with no TTL
   */
  private loadPatientLanguages(): Observable<GenericCode[]> {
    return this.cache.cacheFirst(
      'patient-languages',
      () => this.masterDataRepo.languages(),
      {
        onCacheHit: (languages) => {
          this.patientLanguages.set(languages);

        },
        onFetchComplete: (languages) => {
          this.patientLanguages.set(languages);

        }
      }
    );
  }

  /**
   * Load places of service
   * Uses cache-first strategy with no TTL
   */
  private loadPlacesOfService(): Observable<PlaceOfService[]> {
    return this.cache.cacheFirst(
      'places-of-service',
      () => this.masterDataRepo.placesOfService(),
      {
        onCacheHit: (places) => {
          this.placesOfService.set(places);

        },
        onFetchComplete: (places) => {
          this.placesOfService.set(places);

        }
      }
    );
  }

  /**
   * Load master permissions
   * Uses cache-first strategy with no TTL
   */
  private loadMasterPermissions(): Observable<MasterPermission[]> {
    return this.cache.cacheFirst(
      'master-permissions',
      () => this.masterDataRepo.permissions(),
      {
        onCacheHit: (permissions) => {
          this.masterPermissions.set(permissions);

        },
        onFetchComplete: (permissions) => {
          this.masterPermissions.set(permissions);

        }
      }
    );
  }

  getInsertableValues(...entities: InsertableValueEntity[]): Signal<InsertableValue[]> {
    return this.insertableValuesStore.getInsertableValues(...entities);
  }
}
